import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, logout } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CATEGORIES, BRAND } from "@/data/mockData";
import { Plus, Trash2, Pencil, LogOut, ExternalLink } from "lucide-react";

const OCCASIONS = ["Daily", "Festive", "Wedding", "Pooja", "Gifting", "Corporate"];
const GENDERS = ["Women", "Men", "Kids", "Unisex"];
const PURITIES = ["925 Sterling Silver", "999 Fine Silver"];
const COLLECTIONS = ["daily", "festive", "wedding", "pooja", "gifting", "kids"];

const emptyProduct = () => ({
  name: "", category: "necklaces", collection: "daily",
  price: 0, purity: "925 Sterling Silver", gender: "Women", occasion: "Daily",
  image: "", description: "", in_stock: true, featured: false,
});

export default function AdminDashboard() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [igPosts, setIgPosts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  // product editor
  const [editing, setEditing] = useState(null); // null | 'new' | product obj
  const [form, setForm] = useState(emptyProduct());
  const [saving, setSaving] = useState(false);

  // ig editor
  const [newIg, setNewIg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, i, e, n] = await Promise.all([
        api.get("/products"),
        api.get("/ig-posts"),
        api.get("/enquiries"),
        api.get("/newsletter"),
      ]);
      setProducts(p.data);
      setIgPosts(i.data);
      setEnquiries(e.data);
      setSubscribers(n.data);
    } catch (err) {
      toast("Couldn't load data", { description: err?.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyProduct()); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      name: p.name, category: p.category,
      collection: p.collection_slug || p.collection || "daily",
      price: p.price, purity: p.purity, gender: p.gender, occasion: p.occasion,
      image: p.image, description: p.description || "",
      in_stock: !!p.in_stock, featured: !!p.featured,
    });
    setEditing(p);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/products", { ...form, price: Number(form.price) });
        toast("Product added");
      } else {
        await api.put(`/products/${editing.id}`, { ...form, price: Number(form.price) });
        toast("Product updated");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast("Save failed", { description: err?.response?.data?.detail || err?.message });
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast("Deleted");
      await load();
    } catch (err) { toast("Delete failed", { description: err?.message }); }
  };

  const addIg = async (e) => {
    e.preventDefault();
    if (!newIg.trim()) return;
    try {
      await api.post("/ig-posts", { url: newIg.trim(), order: igPosts.length });
      setNewIg("");
      toast("Instagram post added");
      await load();
    } catch (err) { toast("Couldn't add", { description: err?.message }); }
  };

  const removeIg = async (p) => {
    try {
      await api.delete(`/ig-posts/${p.id}`);
      toast("Removed");
      await load();
    } catch (err) { toast("Delete failed", { description: err?.message }); }
  };

  const doLogout = () => {
    logout();
    setUser(null);
    nav("/admin/login");
  };

  const fmt = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <div data-testid="page-admin" className="min-h-screen bg-[#F8F9FA]">
      {/* Admin top bar */}
      <div className="bg-[#0E0E0E] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={BRAND.logo} alt="SIRIJA" className="h-8 w-8 rounded-full ring-1 ring-white/20 flex-shrink-0" />
            <span className="font-serif tracking-[0.18em]">SIRIJA</span>
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.24em] text-white/40 ml-2">Studio Admin</span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <span className="hidden lg:block text-xs text-white/50 truncate max-w-[180px]">{user?.email}</span>
            <Link to="/" className="text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white flex items-center gap-1">
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} /> <span className="hidden sm:inline">View site</span>
            </Link>
            <button data-testid="admin-logout" onClick={doLogout} className="text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white flex items-center gap-1">
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="overline">Dashboard</div>
            <h1 className="mt-2 font-serif font-light text-4xl lg:text-5xl tracking-tight">Studio.</h1>
          </div>
          <div className="grid grid-cols-4 gap-2 sm:gap-3 text-center w-full lg:w-auto">
            <Metric label="Products" value={products.length} />
            <Metric label="IG posts" value={igPosts.length} />
            <Metric label="Enquiries" value={enquiries.length} />
            <Metric label="Subs" value={subscribers.length} />
          </div>
        </div>

        <Tabs defaultValue="products" className="mt-8">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList data-testid="admin-tabs" className="bg-white border border-[#EDEDED] rounded-full h-11 p-1 inline-flex">
              <TabsTrigger data-testid="tab-products" value="products" className="rounded-full text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] px-3 sm:px-5">Products</TabsTrigger>
              <TabsTrigger data-testid="tab-instagram" value="instagram" className="rounded-full text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] px-3 sm:px-5">Instagram</TabsTrigger>
              <TabsTrigger data-testid="tab-enquiries" value="enquiries" className="rounded-full text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] px-3 sm:px-5">Enquiries</TabsTrigger>
              <TabsTrigger data-testid="tab-newsletter" value="newsletter" className="rounded-full text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] px-3 sm:px-5">Newsletter</TabsTrigger>
            </TabsList>
          </div>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <div className="flex justify-end mb-4">
              <button data-testid="admin-new-product" onClick={openNew} className="h-11 px-5 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.2em] flex items-center gap-2 hover:bg-black transition-colors">
                <Plus className="h-4 w-4" strokeWidth={1.75} /> New product
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-[#EDEDED] overflow-hidden">
              {loading ? (
                <div className="p-10 text-center text-[#A0A0A0] text-sm">Loading…</div>
              ) : products.length === 0 ? (
                <div className="p-10 text-center text-[#A0A0A0] text-sm">No products yet.</div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-[#F8F9FA] text-[#A0A0A0] text-[10px] uppercase tracking-[0.18em]">
                    <tr>
                      <th className="text-left px-4 py-3">Image</th>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Category</th>
                      <th className="text-right px-4 py-3">Price</th>
                      <th className="text-center px-4 py-3 hidden lg:table-cell">In stock</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-t border-[#EDEDED] hover:bg-[#F8F9FA]">
                        <td className="px-4 py-3"><img src={p.image} alt="" className="h-10 w-10 rounded-md object-cover" /></td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-[#A0A0A0]">{p.purity}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-[#2E2E2E]/70">{p.category}</td>
                        <td className="px-4 py-3 text-right">{fmt(p.price)}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-center">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full ${p.in_stock ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${p.in_stock ? "bg-emerald-500" : "bg-neutral-400"}`} />
                            {p.in_stock ? "In stock" : "Sold out"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8 rounded-full hover:bg-black/5 grid place-items-center" aria-label="Edit">
                              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </button>
                            <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p)} className="h-8 w-8 rounded-full hover:bg-red-50 text-red-600 grid place-items-center" aria-label="Delete">
                              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* INSTAGRAM */}
          <TabsContent value="instagram" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5">
                <div className="bg-white p-6 rounded-2xl border border-[#EDEDED]">
                  <h3 className="font-serif text-2xl tracking-tight">Add an Instagram post</h3>
                  <p className="mt-2 text-xs text-[#A0A0A0]">Paste any public post URL from {BRAND.instagramHandle} — for example, <code className="text-[11px]">https://www.instagram.com/p/XXXXXXXX/</code></p>
                  <form onSubmit={addIg} className="mt-5" data-testid="admin-ig-form">
                    <Input
                      required data-testid="admin-ig-url" value={newIg} onChange={(e) => setNewIg(e.target.value)}
                      placeholder="https://www.instagram.com/p/…" className="h-11 rounded-lg border-[#E5E5E5]"
                    />
                    <button type="submit" data-testid="admin-ig-add" className="mt-4 h-11 px-5 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                      <Plus className="h-4 w-4" strokeWidth={1.75} /> Add post
                    </button>
                  </form>
                  <div className="mt-6 text-xs text-[#A0A0A0]">
                    Posts are shown on Home in the order they were added. Rendering uses Instagram's official public embed script — no API keys required.
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="bg-white rounded-2xl border border-[#EDEDED] overflow-hidden">
                  {igPosts.length === 0 ? (
                    <div className="p-10 text-center text-[#A0A0A0] text-sm">No Instagram posts yet.</div>
                  ) : (
                    <ul>
                      {igPosts.map((p) => (
                        <li key={p.id} data-testid={`admin-ig-row-${p.id}`} className="border-b border-[#EDEDED] last:border-b-0 flex items-center gap-4 px-4 py-3">
                          <a href={p.url} target="_blank" rel="noreferrer" className="text-sm flex-1 truncate underline underline-offset-4 hover:no-underline">{p.url}</a>
                          <button data-testid={`admin-ig-delete-${p.id}`} onClick={() => removeIg(p)} className="h-8 w-8 rounded-full hover:bg-red-50 text-red-600 grid place-items-center">
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ENQUIRIES */}
          <TabsContent value="enquiries" className="mt-6">
            <div className="bg-white rounded-2xl border border-[#EDEDED] overflow-hidden">
              {enquiries.length === 0 ? (
                <div className="p-10 text-center text-[#A0A0A0] text-sm">No enquiries yet.</div>
              ) : (
                <div className="divide-y divide-[#EDEDED]">
                  {enquiries.map((e) => (
                    <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-medium">{e.name} <span className="text-[#A0A0A0] font-normal">· {e.email}</span></div>
                          <div className="text-xs text-[#A0A0A0] mt-1">{new Date(e.created_at).toLocaleString()} · {e.source}</div>
                        </div>
                        {e.phone && <a href={`tel:${e.phone}`} className="text-xs text-[#1A1A1A] underline">{e.phone}</a>}
                      </div>
                      {e.subject && <div className="mt-3 text-sm font-medium">{e.subject}</div>}
                      <p className="mt-2 text-sm text-[#2E2E2E]/80 whitespace-pre-wrap">{e.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* NEWSLETTER */}
          <TabsContent value="newsletter" className="mt-6">
            <div className="bg-white rounded-2xl border border-[#EDEDED] overflow-hidden">
              {subscribers.length === 0 ? (
                <div className="p-10 text-center text-[#A0A0A0] text-sm">No subscribers yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F8F9FA] text-[#A0A0A0] text-[10px] uppercase tracking-[0.18em]">
                    <tr><th className="text-left px-4 py-3">Email</th><th className="text-right px-4 py-3">Signed up</th></tr>
                  </thead>
                  <tbody>
                    {subscribers.map((s) => (
                      <tr key={s.id} data-testid={`admin-sub-${s.id}`} className="border-t border-[#EDEDED]">
                        <td className="px-4 py-3">{s.email}</td>
                        <td className="px-4 py-3 text-right text-[#A0A0A0]">{new Date(s.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Editor dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="admin-product-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight">
              {editing === "new" ? "New product" : "Edit product"}
            </DialogTitle>
            <DialogDescription>Fields marked with * are required.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="sm:col-span-2">
              <label className="overline">Name *</label>
              <Input required data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
            </div>
            <div className="sm:col-span-2">
              <label className="overline">Image URL *</label>
              <Input required data-testid="pf-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" placeholder="https://…" />
              {form.image && <img src={form.image} alt="" className="mt-3 h-32 w-full object-cover rounded-lg border border-[#EDEDED]" />}
            </div>
            <div>
              <label className="overline">Price (₹) *</label>
              <Input required type="number" data-testid="pf-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
            </div>
            <div>
              <label className="overline">Category *</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pf-category" className="mt-2 h-11 rounded-lg border-[#E5E5E5]"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline">Collection</label>
              <Select value={form.collection} onValueChange={(v) => setForm({ ...form, collection: v })}>
                <SelectTrigger data-testid="pf-collection" className="mt-2 h-11 rounded-lg border-[#E5E5E5]"><SelectValue /></SelectTrigger>
                <SelectContent>{COLLECTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline">Purity</label>
              <Select value={form.purity} onValueChange={(v) => setForm({ ...form, purity: v })}>
                <SelectTrigger data-testid="pf-purity" className="mt-2 h-11 rounded-lg border-[#E5E5E5]"><SelectValue /></SelectTrigger>
                <SelectContent>{PURITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline">Gender</label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="pf-gender" className="mt-2 h-11 rounded-lg border-[#E5E5E5]"><SelectValue /></SelectTrigger>
                <SelectContent>{GENDERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline">Occasion</label>
              <Select value={form.occasion} onValueChange={(v) => setForm({ ...form, occasion: v })}>
                <SelectTrigger data-testid="pf-occasion" className="mt-2 h-11 rounded-lg border-[#E5E5E5]"><SelectValue /></SelectTrigger>
                <SelectContent>{OCCASIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="overline">Description</label>
              <Textarea rows={3} data-testid="pf-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-lg border-[#E5E5E5]" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border border-[#EDEDED] bg-[#F8F9FA]">
              <div>
                <div className="text-sm font-medium">In stock</div>
                <div className="text-xs text-[#A0A0A0]">Visible in shop</div>
              </div>
              <Switch data-testid="pf-in-stock" checked={form.in_stock} onCheckedChange={(v) => setForm({ ...form, in_stock: v })} />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border border-[#EDEDED] bg-[#F8F9FA]">
              <div>
                <div className="text-sm font-medium">Featured</div>
                <div className="text-xs text-[#A0A0A0]">Appears on home</div>
              </div>
              <Switch data-testid="pf-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button data-testid="pf-cancel" onClick={() => setEditing(null)} className="h-11 px-5 rounded-full border border-[#E5E5E5] text-xs uppercase tracking-[0.18em]">Cancel</button>
            <button data-testid="pf-save" onClick={save} disabled={saving || !form.name || !form.image || !form.price} className="h-11 px-5 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.18em] disabled:opacity-50">
              {saving ? "Saving…" : editing === "new" ? "Add product" : "Save changes"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="px-3 sm:px-4 py-3 rounded-xl bg-white border border-[#EDEDED]">
      <div className="font-serif text-xl sm:text-2xl leading-none">{value}</div>
      <div className="mt-1 text-[9px] sm:text-[10px] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[#A0A0A0] whitespace-nowrap">{label}</div>
    </div>
  );
}
